import { ActionNode } from './nodes/action-node.js';
import { BranchNode } from './nodes/branch-node.js';
import { DoNNode, DoOnceNode, FlipFlopNode, ForLoopWithBreakNode, GateNode, WhileLoopNode, } from './nodes/flow-control-nodes.js';
import { LoopNode } from './nodes/loop-node.js';
import { RerouteNode } from './nodes/reroute-node.js';
import { SequenceNode } from './nodes/sequence-node.js';
import { CallCustomEventNode } from './nodes/call-custom-event-node.js';
import { CallFunctionNode } from './nodes/call-function-node.js';
import { EventNode } from './nodes/event-node.js';
import { FunctionEntryNode } from './nodes/function-entry-node.js';
import { FunctionReturnNode } from './nodes/function-return-node.js';
import { TextToSpeechNode } from './nodes/text-to-speech-node.js';
import { TraySpawnChoiceNode } from './nodes/tray-spawn-choice-node.js';
import { ValueNode } from './nodes/value-node.js';
import { GetVariableNode } from './nodes/get-variable-node.js';
import { SetVariableNode } from './nodes/set-variable-node.js';
import { GraphNode } from './graph-node.js';
import { EVENT_NODE_TYPES, RUN_NODE_TYPES } from './types.js';
import { VALUE_NODE_IMPLEMENTATIONS } from './value-node-implementations.js';
export function createGraphNode(spec, nodeData) {
    const id = spec.id;
    // Copy, never alias: a function body is instantiated per call, and nodes write
    // resolved inputs / Observables back into `data`. Sharing the spec object would
    // let a second call overwrite the inputs of a call that is still running.
    const data = { ...spec.data };
    const type = spec.type;
    const { runImplementations, eventImplementations, subGraphs, variableStore } = nodeData;
    if (type === 'getVariable') {
        return new GetVariableNode(id, data, type, variableStore ?? null);
    }
    if (type === 'setVariable') {
        return new SetVariableNode(id, data, type, variableStore ?? null);
    }
    if (type === 'callFunction') {
        return new CallFunctionNode(id, data, type, nodeData, subGraphs ?? {});
    }
    if (type === 'callCustomEvent') {
        return new CallCustomEventNode(id, data, type);
    }
    if (type === 'functionEntry') {
        return new FunctionEntryNode(id, data, type);
    }
    if (type === 'functionReturn') {
        return new FunctionReturnNode(id, data, type);
    }
    if (type === 'traySpawnChoice') {
        return new TraySpawnChoiceNode(id, data, type, runImplementations.traySpawnChoice ?? null);
    }
    if (type === 'textToSpeech') {
        return new TextToSpeechNode(id, data, type, runImplementations.textToSpeech ?? null);
    }
    if (type === 'reroute') {
        return new RerouteNode(id, data, type);
    }
    if (type === 'branch') {
        return new BranchNode(id, data, type);
    }
    if (type === 'sequence') {
        return new SequenceNode(id, data, type);
    }
    if (type === 'loop') {
        return new LoopNode(id, data, type);
    }
    if (type === 'doOnce') {
        return new DoOnceNode(id, data, type);
    }
    if (type === 'doN') {
        return new DoNNode(id, data, type);
    }
    if (type === 'flipFlop') {
        return new FlipFlopNode(id, data, type);
    }
    if (type === 'gate') {
        return new GateNode(id, data, type);
    }
    if (type === 'whileLoop') {
        return new WhileLoopNode(id, data, type);
    }
    if (type === 'forLoopWithBreak') {
        return new ForLoopWithBreakNode(id, data, type);
    }
    if (EVENT_NODE_TYPES.includes(type)) {
        return new EventNode(id, data, type, eventImplementations[type] ?? null);
    }
    if (RUN_NODE_TYPES.includes(type)) {
        return new ActionNode(id, data, type, runImplementations[type] ?? null);
    }
    const valueNodeImplementation = nodeData.valueImplementations?.[type] ?? VALUE_NODE_IMPLEMENTATIONS.get(type);
    if (valueNodeImplementation) {
        return new ValueNode(id, data, type, valueNodeImplementation.implementation, valueNodeImplementation.isVolatile === true);
    }
    return new GraphNode(id, data, type);
}
